/**
 * Analytics utility for tracking conversions and events
 * Integrates with Google Tag Manager and GA4
 */

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

/**
 * Common analytics event parameters
 */
interface BaseEventParameters {
  event_category?: string;
  event_label?: string;
  [key: string]: any;
}

/**
 * Purchase event parameters
 */
interface PurchaseParameters extends BaseEventParameters {
  value: number;
  currency: string;
  plan: string;
  payment_type: string;
}

/**
 * List of sensitive keys to filter from tracking
 */
const SENSITIVE_KEYS = ["password", "token", "key", "secret", "auth", "private", "credential"];

/**
 * Sanitize parameters to prevent PII exposure
 * @param params - Parameters to sanitize
 * @returns Sanitized parameters
 */
const sanitizeParameters = (params: Record<string, any>): Record<string, any> => {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([key]) => !SENSITIVE_KEYS.some((term) => key.toLowerCase().includes(term)),
    ),
  );
};

/**
 * Track a conversion event in GA4 with error handling
 * @param eventName - The name of the conversion event
 * @param parameters - Additional parameters for the event
 */
export const trackConversion = (eventName: string, parameters?: BaseEventParameters) => {
  try {
    if (typeof window !== "undefined" && window.dataLayer) {
      const sanitizedParams = parameters ? sanitizeParameters(parameters) : {};
      window.dataLayer.push({
        event: eventName,
        ...sanitizedParams,
      });
    }
  } catch (error) {
    // Silently fail - analytics should not break the application
    console.error("Analytics tracking error:", error);
  }
};

/**
 * Track sign up conversion
 * @param method - Sign up method (email, google, github, etc)
 */
export const trackSignUp = (method: string) => {
  trackConversion("sign_up", {
    method,
    event_category: "engagement",
    event_label: "user_signup",
  });
};

/**
 * Track purchase/subscription conversion
 * @param value - Transaction value
 * @param currency - Currency code (USD)
 * @param plan - Subscription plan name
 * @param paymentType - Payment type (monthly, yearly)
 */
export const trackPurchase = (
  value: number,
  currency: string,
  plan: string,
  paymentType: string,
) => {
  // Validate required parameters
  if (typeof value !== "number" || value <= 0 || isNaN(value)) {
    console.error("trackPurchase: Invalid value parameter");
    return;
  }

  if (!currency || currency.length !== 3) {
    console.error("trackPurchase: Invalid currency parameter");
    return;
  }

  if (!plan || !paymentType) {
    console.error("trackPurchase: Missing required parameters");
    return;
  }

  const params: PurchaseParameters = {
    value: Number(value.toFixed(2)), // Ensure 2 decimal places
    currency: currency.toUpperCase(),
    plan,
    payment_type: paymentType,
    event_category: "ecommerce",
    event_label: "subscription_purchase",
  };

  trackConversion("purchase", params);
};

/**
 * Track first monitor creation
 * @param monitorType - Type of monitor (https, tcp, ping, certificate)
 */
export const trackFirstMonitor = (monitorType: string) => {
  trackConversion("first_monitor_created", {
    monitor_type: monitorType,
    event_category: "engagement",
    event_label: "monitor_setup",
  });
};

/**
 * Track integration setup
 * @param integrationType - Type of integration (pagerduty, zendesk, slack, etc)
 */
export const trackIntegrationSetup = (integrationType: string) => {
  trackConversion("integration_setup", {
    integration_type: integrationType,
    event_category: "engagement",
    event_label: "integration_connected",
  });
};

/**
 * Track A/B test variant assignment for payment gate experiment
 * @param requirePaymentMethod - Whether the user is in the require_pm variant
 */
export const trackAbVariant = (requirePaymentMethod: boolean) => {
  try {
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "ab_variant_set",
        user_properties: {
          ab_payment_gate: requirePaymentMethod ? "require_pm" : "control",
        },
      });
    }
  } catch (error) {
    console.error("Analytics tracking error:", error);
  }
};
